import { useEffect, useMemo, useRef, useState } from 'react'
import {
  getFieldErrors,
  personalInfoSchema,
  academicInfoSchema,
  athleticInfoSchema,
  studentProfileSchema,
  PERSONAL_RECORDS_COVERAGE_MESSAGE,
  getAllowedSchoolYears,
} from '@tnf-recruit/shared'
import { useStudentProfile } from './useStudentProfile'
import { useAuth } from './useAuth'
import { formatUsPhone } from '../lib/phone'

export type ProfileDataState = {
  personalInfo: {
    firstName: string
    lastName: string
    email: string
    mobilePhone: string
    country: string
    address: {
      line1: string
      line2: string
      city: string
      state: string
      zipCode: string
    }
    dateOfBirth: string
    genderIdentity: string
    genderIdentityDetails: string
    competitionTeam: string
    bio: string
    parentGuardian: {
      firstName: string
      lastName: string
      email: string
      phoneNumber: string
    }
  }
  academicInfo: {
    highSchool: string
    graduationYear: string
    gpa: string
    satScore: string
    actScore: string
  }
  athleticInfo: {
    ncaaId: string
    height: {
      // '' = not yet selected; the dropdowns start on the label as a prompt and the
      // required validation fails until a real value is chosen.
      feet: number | ''
      inches: number | ''
    }
    position: string
    primaryEvents: string[]
    recruitingVideoLink: string
    clubTeam: string
    clubPosition: string
    plannedFirstYearOfCollege: string
  }
}

/** Per-PR metadata not shown in the mark input, carried forward on save so it
 * isn't silently wiped (see buildStudentProfilePayload). */
type PrMeta = {
  date?: Date
  venue?: string
  isVerified?: boolean
  isOutdoor?: boolean
}

const emptyProfileData: ProfileDataState = {
  personalInfo: {
    firstName: '',
    lastName: '',
    email: '',
    mobilePhone: '',
    country: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      zipCode: '',
    },
    dateOfBirth: '',
    genderIdentity: '',
    genderIdentityDetails: '',
    competitionTeam: '',
    bio: '',
    parentGuardian: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
    },
  },
  academicInfo: {
    highSchool: '',
    graduationYear: '',
    gpa: '',
    satScore: '',
    actScore: '',
  },
  athleticInfo: {
    ncaaId: '',
    height: {
      feet: '',
      inches: '',
    },
    position: '',
    primaryEvents: [] as string[],
    recruitingVideoLink: '',
    clubTeam: '',
    clubPosition: '',
    plannedFirstYearOfCollege: '',
  },
}

function buildStudentProfilePayload(
  profileData: ProfileDataState,
  prMarks: Record<string, string>,
  prMeta: Record<string, PrMeta>,
  isPublic?: boolean
) {
  const personalRecords = profileData.athleticInfo.primaryEvents
    .map(eventId => {
      const mark = prMarks[eventId]?.trim()
      if (!mark) return null
      const value = Number.parseFloat(mark)
      if (!Number.isFinite(value) || value <= 0) return null
      const meta = prMeta[eventId]
      return {
        eventId,
        mark,
        value,
        date: meta?.date,
        // Carry forward whatever we already had for this PR — a routine edit
        // to the mark shouldn't blank out venue/outdoor/verified metadata the
        // student (or an admin) previously set.
        venue: meta?.venue ?? '',
        isOutdoor: meta?.isOutdoor ?? true,
        isVerified: meta?.isVerified ?? false,
      }
    })
    .filter((record): record is NonNullable<typeof record> => record !== null)

  return {
    personalInfo: {
      ...profileData.personalInfo,
      dateOfBirth: profileData.personalInfo.dateOfBirth || undefined,
    } as any,
    academicInfo: {
      highSchool: profileData.academicInfo.highSchool,
      graduationYear: Number(profileData.academicInfo.graduationYear) || undefined,
      gpa: Number(profileData.academicInfo.gpa) || undefined,
      satScore: profileData.academicInfo.satScore
        ? Number(profileData.academicInfo.satScore)
        : undefined,
      actScore: profileData.academicInfo.actScore
        ? Number(profileData.academicInfo.actScore)
        : undefined,
    } as any,
    athleticInfo: {
      ...profileData.athleticInfo,
      recruitingVideoLink: profileData.athleticInfo.recruitingVideoLink || undefined,
      plannedFirstYearOfCollege:
        Number(profileData.athleticInfo.plannedFirstYearOfCollege) || undefined,
    } as any,
    personalRecords,
    ...(isPublic !== undefined ? { isPublic } : {}),
  }
}

function validatePrMarksForPrimaryEvents(
  primaryEvents: string[],
  prMarks: Record<string, string>
): { ok: true } | { ok: false; fieldErrors: Record<string, string>; message: string } {
  const fieldErrors: Record<string, string> = {}
  for (const eventId of primaryEvents) {
    const mark = prMarks[eventId]?.trim()
    if (!mark) {
      fieldErrors[`prMark.${eventId}`] = 'Add your best mark (PR) for this event.'
      continue
    }
    const value = Number.parseFloat(mark)
    if (!Number.isFinite(value) || value <= 0) {
      fieldErrors[`prMark.${eventId}`] = 'Use a valid mark (a positive number).'
    }
  }
  if (Object.keys(fieldErrors).length > 0) {
    return { ok: false, fieldErrors, message: PERSONAL_RECORDS_COVERAGE_MESSAGE }
  }
  return { ok: true }
}

const isMissingOrPlaceholderEmail = (value: string) =>
  !value.trim() || value.includes('@placeholder.tnf-recruit.com')

/**
 * Owns all athlete-profile form state, hydration, validation, and saving.
 * Shared by the onboarding wizard (`AthleteProfileWizard`) and the single-page
 * edit form (`AthleteProfileEditForm`) — they differ only in chrome.
 */
export function useAthleteProfileForm() {
  const { user: authUser } = useAuth()
  const { profile, saveProfile } = useStudentProfile()
  const [data, setData] = useState<ProfileDataState>(emptyProfileData)
  const [prMarks, setPrMarks] = useState<Record<string, string>>({})
  const [prMeta, setPrMeta] = useState<Record<string, PrMeta>>({})
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  // Guards against a post-save refetch clobbering in-progress edits — the
  // wizard only ever saves once (onboarding), but the edit page stays mounted
  // after a save, and `profile.data` changes identity on every invalidation.
  const hydratedRef = useRef(false)

  const authEmail = authUser?.email ?? ''

  const schoolYearOptions = useMemo(() => {
    const base = getAllowedSchoolYears()
    const years = new Set(base)
    for (const raw of [
      data.academicInfo.graduationYear,
      data.athleticInfo.plannedFirstYearOfCollege,
    ]) {
      const n = Number(raw)
      if (raw !== '' && Number.isFinite(n)) years.add(n)
    }
    return [...years].sort((a, b) => a - b)
  }, [data.academicInfo.graduationYear, data.athleticInfo.plannedFirstYearOfCollege])

  // Hydrate form from API data when the profile first loads.
  useEffect(() => {
    if (!profile.data || hydratedRef.current) return
    hydratedRef.current = true

    const p = profile.data
    const pi = p.personalInfo ?? ({} as any)
    const ai = p.academicInfo ?? ({} as any)
    const ath = p.athleticInfo ?? ({} as any)

    setData({
      personalInfo: {
        firstName: pi.firstName ?? '',
        lastName: pi.lastName ?? '',
        email: isMissingOrPlaceholderEmail(pi.email ?? '') ? authEmail : (pi.email ?? ''),
        mobilePhone: formatUsPhone(pi.mobilePhone ?? ''),
        country: pi.country ?? '',
        address: {
          line1: pi.address?.line1 ?? '',
          line2: pi.address?.line2 ?? '',
          city: pi.address?.city ?? '',
          state: pi.address?.state ?? '',
          zipCode: pi.address?.zipCode ?? '',
        },
        dateOfBirth: pi.dateOfBirth ? new Date(pi.dateOfBirth).toISOString().split('T')[0] : '',
        genderIdentity: pi.genderIdentity ?? '',
        genderIdentityDetails: pi.genderIdentityDetails ?? '',
        competitionTeam: pi.competitionTeam ?? '',
        bio: pi.bio ?? '',
        parentGuardian: {
          firstName: pi.parentGuardian?.firstName ?? '',
          lastName: pi.parentGuardian?.lastName ?? '',
          email: pi.parentGuardian?.email ?? '',
          phoneNumber: formatUsPhone(pi.parentGuardian?.phoneNumber ?? ''),
        },
      },
      academicInfo: {
        highSchool: ai.highSchool ?? '',
        graduationYear: ai.graduationYear?.toString() ?? '',
        gpa: ai.gpa?.toString() ?? '',
        satScore: ai.satScore?.toString() ?? '',
        actScore: ai.actScore?.toString() ?? '',
      },
      athleticInfo: {
        ncaaId: ath.ncaaId ?? '',
        height: {
          feet: ath.height?.feet ?? '',
          inches: ath.height?.inches ?? '',
        },
        position: ath.position ?? '',
        primaryEvents: ath.primaryEvents ?? [],
        recruitingVideoLink: ath.recruitingVideoLink ?? '',
        clubTeam: ath.clubTeam ?? '',
        clubPosition: ath.clubPosition ?? '',
        plannedFirstYearOfCollege: ath.plannedFirstYearOfCollege?.toString() ?? '',
      },
    })

    if (p.personalRecords) {
      const marks: Record<string, string> = {}
      const meta: Record<string, PrMeta> = {}
      for (const r of p.personalRecords) {
        marks[r.eventId] = r.mark
        meta[r.eventId] = {
          date: r.date ? new Date(r.date) : undefined,
          venue: r.venue,
          isVerified: r.isVerified,
          isOutdoor: r.isOutdoor,
        }
      }
      setPrMarks(marks)
      setPrMeta(meta)
    }
  }, [profile.data, authEmail])

  // Seed email from auth when there's no profile yet (new athlete).
  useEffect(() => {
    if (profile.data) return
    setData(prev => ({
      ...prev,
      personalInfo: {
        ...prev.personalInfo,
        email: isMissingOrPlaceholderEmail(prev.personalInfo.email)
          ? authEmail
          : prev.personalInfo.email,
      },
    }))
  }, [profile.data, authEmail])

  const setErrorForField = (
    fullPath: string,
    schemaKey: string,
    errors: Record<string, string>
  ) => {
    setFieldErrors(prev => {
      const next = { ...prev }
      const err = errors[schemaKey]
      if (err) next[fullPath] = err
      else delete next[fullPath]
      return next
    })
  }

  const validatePersonalField = (
    schemaKey: string,
    fullPath: string,
    nextPersonal: ProfileDataState['personalInfo']
  ) => {
    const body = { ...nextPersonal, dateOfBirth: nextPersonal.dateOfBirth || undefined }
    const errors = getFieldErrors(personalInfoSchema, body)
    setErrorForField(fullPath, schemaKey, errors)
  }

  const validateAcademicField = (
    schemaKey: string,
    fullPath: string,
    nextAcademic: ProfileDataState['academicInfo']
  ) => {
    const body = {
      highSchool: nextAcademic.highSchool,
      graduationYear: Number(nextAcademic.graduationYear) || undefined,
      gpa: Number(nextAcademic.gpa) || undefined,
      satScore: nextAcademic.satScore ? Number(nextAcademic.satScore) : undefined,
      actScore: nextAcademic.actScore ? Number(nextAcademic.actScore) : undefined,
    }
    const errors = getFieldErrors(academicInfoSchema, body)
    setErrorForField(fullPath, schemaKey, errors)
  }

  const validateAthleticField = (
    schemaKey: string,
    fullPath: string,
    nextAthletic: ProfileDataState['athleticInfo']
  ) => {
    const body = {
      ...nextAthletic,
      plannedFirstYearOfCollege: Number(nextAthletic.plannedFirstYearOfCollege) || undefined,
    }
    const errors = getFieldErrors(athleticInfoSchema, body)
    setErrorForField(fullPath, schemaKey, errors)
  }

  /** Per-step validation for the wizard. Returns whether the step is valid. */
  const validateStep = (step: 0 | 1 | 2): boolean => {
    if (step === 0) {
      const errors = getFieldErrors(personalInfoSchema, {
        ...data.personalInfo,
        dateOfBirth: data.personalInfo.dateOfBirth || undefined,
      })
      if (Object.keys(errors).length > 0) {
        const prefixed: Record<string, string> = {}
        for (const [k, v] of Object.entries(errors)) prefixed[`personalInfo.${k}`] = v
        setFieldErrors(prefixed)
        return false
      }
    } else if (step === 1) {
      const built = {
        highSchool: data.academicInfo.highSchool,
        graduationYear: Number(data.academicInfo.graduationYear) || undefined,
        gpa: Number(data.academicInfo.gpa) || undefined,
        satScore: data.academicInfo.satScore ? Number(data.academicInfo.satScore) : undefined,
        actScore: data.academicInfo.actScore ? Number(data.academicInfo.actScore) : undefined,
      }
      const errors = getFieldErrors(academicInfoSchema, built)
      if (Object.keys(errors).length > 0) {
        const prefixed: Record<string, string> = {}
        for (const [k, v] of Object.entries(errors)) prefixed[`academicInfo.${k}`] = v
        setFieldErrors(prefixed)
        return false
      }
    } else if (step === 2) {
      const built = {
        ...data.athleticInfo,
        plannedFirstYearOfCollege: Number(data.athleticInfo.plannedFirstYearOfCollege) || undefined,
      }
      const errors = getFieldErrors(athleticInfoSchema, built)
      if (Object.keys(errors).length > 0) {
        const prefixed: Record<string, string> = {}
        for (const [k, v] of Object.entries(errors)) prefixed[`athleticInfo.${k}`] = v
        setFieldErrors(prefixed)
        return false
      }
    }
    setFieldErrors({})
    return true
  }

  /** Full-payload validation used before any save (wizard's last step, or the single-page edit form). */
  const validateAll = (): boolean => {
    const body = buildStudentProfilePayload(data, prMarks, prMeta)
    setFieldErrors(prev => {
      const next = { ...prev }
      for (const k of Object.keys(next)) {
        if (k.startsWith('prMark.')) delete next[k]
      }
      delete next.personalRecords
      return next
    })
    const prCheck = validatePrMarksForPrimaryEvents(data.athleticInfo.primaryEvents, prMarks)
    if (!prCheck.ok) {
      setFieldErrors(prev => ({
        ...prev,
        ...prCheck.fieldErrors,
        personalRecords: prCheck.message,
      }))
      setSaveError(prCheck.message)
      return false
    }
    const errors = getFieldErrors(studentProfileSchema, body)
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      return false
    }
    setFieldErrors({})
    setSaveError(null)
    return true
  }

  const save = async (opts?: { isPublic?: boolean }): Promise<boolean> => {
    if (!validateAll()) return false
    try {
      const body = buildStudentProfilePayload(data, prMarks, prMeta, opts?.isPublic)
      await saveProfile.mutateAsync(body)
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
      return true
    } catch (error: any) {
      setSaveError(error?.response?.data?.error ?? 'Failed to save profile. Please try again.')
      return false
    }
  }

  return {
    isLoading: profile.isLoading,
    data,
    setData,
    prMarks,
    setPrMarks,
    fieldErrors,
    setFieldErrors,
    schoolYearOptions,
    validatePersonalField,
    validateAcademicField,
    validateAthleticField,
    validateStep,
    validateAll,
    save,
    isSaving: saveProfile.isPending,
    isSaveHttpError: saveProfile.isError,
    saveError,
    saved,
  }
}

export type AthleteProfileFormApi = ReturnType<typeof useAthleteProfileForm>
